import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Loader2, TrendingDown, TrendingUp, RefreshCw, Target, Clock, AlertTriangle, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ITEMS_PER_PAGE = 20;

export default function PlanningPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [burndownData, setBurndownData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingBurndown, setLoadingBurndown] = useState(false);
  const [redistributing, setRedistributing] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);

  // All users can redistribute hours for their projects
  const canRedistribute = true;

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchBurndown(selectedProject);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects`);
      setProjects(response.data.filter(p => p.is_active));
      if (response.data.length > 0 && !selectedProject) {
        setSelectedProject(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchBurndown = async (projectId) => {
    setLoadingBurndown(true);
    try {
      const response = await axios.get(`${API_URL}/api/projects/${projectId}/burndown`);
      setBurndownData(response.data);
    } catch (error) {
      if (error.response?.status === 400) {
        setBurndownData(null);
      } else {
        toast.error('Failed to load burndown data');
      }
    } finally {
      setLoadingBurndown(false);
    }
  };

  const handleRedistribute = async () => {
    if (!selectedProject) return;
    setRedistributing(true);
    try {
      await axios.post(`${API_URL}/api/projects/${selectedProject}/distribute-all`);
      toast.success('Hours redistributed successfully');
      fetchBurndown(selectedProject);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to redistribute hours');
    } finally {
      setRedistributing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'on_track': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'behind': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      case 'over': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'on_track': return 'On Track';
      case 'behind': return 'Behind Schedule';
      case 'over': return 'Over Budget';
      default: return status;
    }
  };

  const formatChartData = (dataPoints, totalAllocated) => {
    if (!dataPoints) return [];
    return dataPoints.map(dp => ({
      ...dp,
      date: dp.date.substring(5), // Show MM-DD
      target: totalAllocated, // Flat target line
    }));
  };

  // Calculate daily values from cumulative data
  const dailyBreakdownData = useMemo(() => {
    if (!burndownData?.data_points) return [];
    return burndownData.data_points.map((dp, idx) => {
      const prevPoint = idx > 0 ? burndownData.data_points[idx - 1] : null;
      return {
        date: dp.date,
        dailyPlanned: prevPoint ? dp.planned_cumulative - prevPoint.planned_cumulative : dp.planned_cumulative,
        dailyActual: prevPoint ? dp.actual_cumulative - prevPoint.actual_cumulative : dp.actual_cumulative,
        cumulativePlanned: dp.planned_cumulative,
        cumulativeActual: dp.actual_cumulative,
        remaining: dp.remaining_planned,
      };
    });
  }, [burndownData]);

  // Sorting logic
  const sortedData = useMemo(() => {
    if (!dailyBreakdownData.length) return [];
    const sorted = [...dailyBreakdownData];
    sorted.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [dailyBreakdownData, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedData.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedData, currentPage]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1);
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <ChevronsUpDown className="w-4 h-4 ml-1 opacity-50" />;
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-4 h-4 ml-1" />
      : <ChevronDown className="w-4 h-4 ml-1" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Project Planning</h1>
          <p className="text-muted-foreground">View burndown charts and redistribute planned hours</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={selectedProject || ''} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-[250px]" data-testid="project-select">
              <SelectValue placeholder="Select a project" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  {project.name} ({project.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={handleRedistribute}
            disabled={redistributing || !selectedProject}
            variant="outline"
            data-testid="redistribute-btn"
          >
            {redistributing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Redistribute Hours
          </Button>
        </div>
      </div>

      {loadingBurndown ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : burndownData ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Allocated</CardTitle>
                <Target className="w-4 h-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{burndownData.total_allocated}h</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Logged</CardTitle>
                <Clock className="w-4 h-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-primary">{burndownData.total_logged}h</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Remaining</CardTitle>
                {burndownData.variance_percentage > 0 ? (
                  <TrendingUp className="w-4 h-4 text-red-500" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-emerald-500" />
                )}
              </CardHeader>
              <CardContent>
                <div className={`text-3xl font-bold ${burndownData.total_remaining < 0 ? 'text-red-600' : ''}`}>
                  {burndownData.total_remaining}h
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Status</CardTitle>
                <AlertTriangle className="w-4 h-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <Badge className={getStatusColor(burndownData.status)}>
                  {getStatusLabel(burndownData.status)}
                </Badge>
                <p className="text-xs text-muted-foreground mt-1">
                  {burndownData.variance_percentage > 0 ? '+' : ''}{burndownData.variance_percentage.toFixed(1)}% variance
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Burndown Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Burndown Chart</CardTitle>
            </CardHeader>
            <CardContent>
              {burndownData.data_points.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={formatChartData(burndownData.data_points, burndownData.total_allocated)}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      className="text-muted-foreground"
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }}
                      className="text-muted-foreground"
                      label={{ value: 'Hours', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                      labelStyle={{ color: 'hsl(var(--foreground))' }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="target"
                      stroke="hsl(0 84.2% 60.2%)"
                      strokeWidth={2}
                      strokeDasharray="8 4"
                      name="Target (Allocated)"
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="ideal_remaining"
                      stroke="hsl(var(--muted-foreground))"
                      strokeDasharray="5 5"
                      name="Ideal Remaining"
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="actual_cumulative"
                      stroke="hsl(142.1 76.2% 36.3%)"
                      strokeWidth={2}
                      name="Actual Logged"
                    />
                    <Line
                      type="monotone"
                      dataKey="planned_cumulative"
                      stroke="hsl(38.3 92% 50%)"
                      strokeWidth={2}
                      name="Planned Cumulative"
                    />
                    <ReferenceLine y={0} stroke="hsl(var(--border))" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  No data points available. Click "Redistribute Hours" to generate the plan.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Data Table */}
          {burndownData.data_points.length > 0 && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Daily Breakdown</CardTitle>
                <span className="text-sm text-muted-foreground">
                  {sortedData.length} workdays total
                </span>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('date')}
                        >
                          <div className="flex items-center">
                            Date
                            <SortIcon column="date" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="text-right cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('dailyPlanned')}
                        >
                          <div className="flex items-center justify-end">
                            Planned
                            <SortIcon column="dailyPlanned" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="text-right cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('dailyActual')}
                        >
                          <div className="flex items-center justify-end">
                            Actual
                            <SortIcon column="dailyActual" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="text-right cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('cumulativePlanned')}
                        >
                          <div className="flex items-center justify-end">
                            Cum. Planned
                            <SortIcon column="cumulativePlanned" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="text-right cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('cumulativeActual')}
                        >
                          <div className="flex items-center justify-end">
                            Cum. Actual
                            <SortIcon column="cumulativeActual" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="text-right cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('remaining')}
                        >
                          <div className="flex items-center justify-end">
                            Remaining
                            <SortIcon column="remaining" />
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedData.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{row.date}</TableCell>
                          <TableCell className="text-right">{row.dailyPlanned.toFixed(1)}h</TableCell>
                          <TableCell className="text-right text-primary">
                            {row.dailyActual.toFixed(1)}h
                          </TableCell>
                          <TableCell className="text-right">{row.cumulativePlanned.toFixed(1)}h</TableCell>
                          <TableCell className="text-right">{row.cumulativeActual.toFixed(1)}h</TableCell>
                          <TableCell className={`text-right ${row.remaining < 0 ? 'text-red-600' : ''}`}>
                            {row.remaining.toFixed(1)}h
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t">
                    <span className="text-sm text-muted-foreground">
                      Page {currentPage} of {totalPages}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                      >
                        First
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        Last
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Target className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No Planning Data</h3>
            <p className="text-muted-foreground mt-1">
              {selectedProject 
                ? 'This project has no assignments or dates configured. Add assignments with start/end dates to enable planning.'
                : 'Select a project to view its burndown chart.'
              }
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
